// DO NOT EDIT -- generated from fs/api/pages/pages.proto

import * as protoauditstore from '../../gcloud/dstore/protoauditstore';
import * as protobuf from '../../../google/protobuf';
import * as segment from '../segment';

/**
 * PageDefinition represents a defined page, which is a human-readable label such as Checkout,
 * along with rules for determining when someone has visited that page.
 */
export interface PageDefinition {
  id: number;
  /** friendly id for use in URLs, displaying in the UI */
  friendlyId: string;
  name: string;
  description: string;
  labels: LabelRule[];
  patterns: PatternRule[];
  /** True if this page is defined by a user, rather than managed by FullStory's algorithms. */
  isUserDefined: boolean;
  /**
   * TODO: These are moving to the auditmetadata struct.
   * 
   * @deprecated
   */
  archived: boolean;
  /** @deprecated */
  createdBy: string;
  /** @deprecated */
  createdAt: protobuf.timestamp | null;
  /** @deprecated */
  updatedBy: string;
  /** @deprecated */
  updatedAt: protobuf.timestamp | null;
  /** after this date, the definition should be deleted */
  expiration: protobuf.timestamp | null;
  metadata: protoauditstore.AuditMetadata | null;
  suggestion: PageSuggestion | null;
}

/**
 * LabelRule associate an event with a page via the fs.setVars API, which accepts a "page name" property.
 * Calls to fs.setVars with this page name will match the rule.
 */
export interface LabelRule {
  pageName: string;
  platforms: Platform[];
}

export interface Platform {
  platform: segment.NativePlatform;
  lastSeen: protobuf.timestamp | null;
}

/**
 * PatternRule associates an event with a page via its URL. The rule is a glob-like pattern that matches
 * against a page's URL. For example:
 *     example.com/foo/[bar, baz]/*
 * would match against URLs such as
 *     example.com/foo/bar/zoom
 *     example.com/foo/baz/zap
 * but not
 *     example.com/foo/bar
 *     example.com/bar/foo
 *     invalid.com/foo/bar
 * Events whose URL conform to the pattern will match the rule.
 */
export interface PatternRule {
  path: Term[];
  fragment: Term[];
  domains: Domain[];
  /**
   * The set of query parameters that must match for this pattern rule to match.
   * 
   * Ordering of query parameters is not differentiated -- matching is based on key value entries.
   * 
   * The existence of a query parameter in this set enables page view emits for every
   * query parameter value change within the scope of the rest of the rule, regardless
   * of whether or not that query parameter value matches an existing rule.
   */
  query: Query[];
}

export interface Domain {
  terms: Term[];
}

export interface Query {
  /** The query param key. */
  key: string;
  /**
   * The query param value term.
   * Typically, this will be a single or multi value literal.
   * Wildcard can be used to match query param key existence (regardless of value).
   */
  value: Term | null;
}

/**
 * Term is a single component in a PatternRule. The position of the Term is important,
 * so patterns foo/bar and bar/foo are different.
 */
export interface Term {
  /** match a single literal ("foo") */
  literal?: string;
  /** match any in a group of literals ("foo", "bar", or "baz") */
  group?: LiteralGroup | null;
  /** match any literal at this position, or any number of literals */
  wildcard?: Wildcard | null;
}

/**
 * Term is a single component in a PatternRule. The position of the Term is important,
 * so patterns foo/bar and bar/foo are different.
 */
export namespace Term {
  export enum kind {
    /** match a single literal ("foo") */
    literal = 1,
    /** match any in a group of literals ("foo", "bar", or "baz") */
    group = 2,
    /** match any literal at this position, or any number of literals */
    wildcard = 3
  }

  export function kind_oneof(m: Term): { opt: kind.group, val: LiteralGroup } | { opt: kind.literal, val: string } | { opt: kind.wildcard, val: Wildcard } | null {
    if (m.literal != null) { return { opt: kind.literal, val: m.literal } }
    if (m.group != null) { return { opt: kind.group, val: m.group } }
    if (m.wildcard != null) { return { opt: kind.wildcard, val: m.wildcard } }
    return null;
  }
}

/**
 * LiteralGroup is a group if literal groups which signify an
 * "or" statement in a PatternRule.
 */
export interface LiteralGroup {
  literals: string[];
}

export interface Wildcard {
  /** double wildcard if true, otherwise single wildcard */
  double: boolean;
}

/** Conflict describes overlapping or ambiguous rules that prevent a modification from occurring. */
export interface Conflict {
  proposedPage: PageDefinition | null;
  proposedRule: PatternRule | null;
  existingPage: PageDefinition | null;
  existingRule: PatternRule | null;
}

export enum SuggestionPriority {
  PRIORITY_UNSET = 0,
  PRIORITY_LOW = 1,
  PRIORITY_MEDIUM = 2,
  PRIORITY_HIGH = 3
}

export enum SuggestionStatus {
  STATUS_UNSET = 0,
  STATUS_PENDING = 1,
  STATUS_APPROVED = 2,
  STATUS_AUTO_APPROVED = 6,
  STATUS_FS_REJECTED = 3,
  STATUS_USER_REJECTED = 4,
  STATUS_EXPIRED = 5
}

export interface PageArgs {
  userId: protobuf.int64;
  sessionId: protobuf.int64;
  pageId: protobuf.int64;
  pageStartTimeMs: protobuf.int64;
  pageOffsetMs: protobuf.int64;
  urlFull: string;
}

export interface PageSuggestion {
  id: string;
  status: SuggestionStatus;
  priority: SuggestionPriority;
  justification: string;
  description: string;
  spec: string;
  patterns: PatternRule[];
  name: string;
  /** AI judged quality score of page rules */
  score: number;
  /** Page def id reserved for when the suggestion is saved to a full page */
  reservedPageId: number;
  thumbnailPageArgs: PageArgs[];
}
